import React, {useState, useEffect, useContext, useMemo, useRef} from "react";
import {useParams} from "react-router-dom";
import PropTypes from "prop-types";
import styled, {css} from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faLock, faTriangleExclamation} from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// Hooks
import useMountedState from "../../hooks/useMountedState.js";
import useApi from "../../hooks/useApi.js";

// Contexts
import {useSocket} from "../../contexts/socket.js";
import {AuthContext} from "../../contexts/auth.js";
import {NotificationContext} from "../../contexts/notify.js";
import {SettingsContext} from "../../contexts/settings.js";
import {NavContext} from "../../contexts/nav.js";
import {FacilityNavContext} from "../../contexts/facilitynav.js";

// Utils
import {formatSubmissions} from "../../utils/builder.js";
import {getCompletionRate, prettyDateInUserTimezone} from "../../utils/helpers.js";
import {getWeatherSourceLinks} from "../../utils/weather-links.js";
import {isMobile} from "../../utils/responsive.js";
import {filterReviewsForGate, toWeatherContext} from "./reviewConditions.js";

// Components
import RenderChecksheet from "../checksheet-builder/RenderChecksheet.js";
import PrecipitationChanceIndicator from "../../components/PrecipitationChanceIndicator.js";
import Badge from "../../components/Badge.js";
import ModalReviews from "./ModalReviews.js";
import ModalReview from "./ModalReview.js";
import ModalRecordView from "../checksheets/ModalRecordView.js";

// Style
import {bp} from "../../style/components/breakpoints.js";
import {border, pad, radius, heroTheme} from "../../style/components/variables.js";
import {flex} from "../../style/components/mixins.js";
import {Abbr, Button, Inline, Pill, Small, Text, Title} from "../../style/components/general.js";

// Socket Constants
import {LOCK, RELEASE, getFacilityRooms} from "../general/Room.js";

dayjs.extend(utc);
dayjs.extend(timezone);

const NOTIFY_COMPLETED = "notify_completed";
const NOTIFY_DRAFT = "notify_draft";

const ChecksheetDue = ({
  room,
  locks = {},
  checksheet,
  sharedResponses = {},
  completing,
  setCompleting,
  setHide,
  setSharedResponses,
  openingIdRef
}) => {
  const {slug} = useParams();

  const isMounted = useMountedState();

  const socket = useSocket();

  const {allStates} = useContext(NavContext);
  const {settings} = useContext(SettingsContext);

  const {currentUser, roleCanAccessResource, atLeast} = useContext(AuthContext);

  const {updateChecksheetInfo} = useContext(NotificationContext);
  const {facility} = useContext(FacilityNavContext);

  const {api: apiChecksheets} = useApi("checksheets");
  const {api: apiChecksheetRecords} = useApi("checksheet-records");
  // Separate instance so the previous-submission prefetch below cannot abort an in-flight
  // save (useApi shares one AbortController per instance, and each call aborts the prior one).
  const {api: apiPrevSubmission} = useApi("checksheet-records");
  const {api: apiUsers} = useApi("users");

  const [targetRecord, setTargetRecord] = useState();
  const [keyHolder, setKeyHolder] = useState(null);
  const [completeLoading, setCompleteLoading] = useState(null);
  const [draftLoading, setDraftLoading] = useState(null);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [prevSubmission, setPrevSubmission] = useState(null);
  // Modals
  const [showModalReviews, setShowModalReviews] = useState(false);
  const [showModalReviewsRequired, setShowModalReviewsRequired] = useState(false);
  // True after this tab receives locks[key] for the open form — used to close on multi-session release.
  const heldLockRef = useRef(false);
  // Socket that took the lock — the server drops locks when that socket disconnects.
  const lockSocketIdRef = useRef(null);
  const relockingRef = useRef(false);

  const hasFullBuilder = Boolean(checksheet?.builder?.allIds?.length);

  const isOptional = !checksheet?.frequency;

  const [lastCompletedAt, setLastCompletedAt] = useState(checksheet.lastCompletedAt ?? null);

  useEffect(() => {
    // A full-view fetch (fetchFullChecksheet, view="full") replaces the whole checksheet
    // object via updateChecksheetInfo(id, data) with no key, and that view never serializes
    // lastCompletedAt — only resync from an explicit value, never from "missing".
    if (checksheet.lastCompletedAt !== undefined) setLastCompletedAt(checksheet.lastCompletedAt);
  }, [checksheet.lastCompletedAt]);

  const fetchFullChecksheet = () =>
    apiChecksheets.callGet(checksheet.id, {
      withDrafts: true,
      withCurrentState: true
    });

  const key = `checksheet_${checksheet.id}`;

  // Prefer the completing payload after lazy full fetch so RenderChecksheet does not wait
  // on NotificationContext merge.
  const activeChecksheet =
    completing?.id === checksheet.id && completing?.builder?.allIds?.length
      ? completing
      : checksheet;

  const recordDraftUser = useMemo(() => {
    const user = activeChecksheet?.recordDraft?.updatedBy || activeChecksheet?.recordDraft?.user;
    if (user === "GENERATED") return undefined;
    return user;
  }, [activeChecksheet]);

  useEffect(() => {
    if (completing?.id === checksheet.id)
      apiPrevSubmission
        .callGet(null, {facilityChecksheetId: checksheet.id, getPreviousSubmission: true})
        .then(response => {
          if (response?.status === 200 && response?.data) {
            setPrevSubmission(response.data);
          }
        });
  }, [apiPrevSubmission, checksheet.id, completing?.id]);

  // Set "Key Holder" from current locks; close open form if we held the lock and then lost it.
  useEffect(() => {
    if (!isMounted() || !currentUser?.publicId || !locks) return;

    const lock = locks[key];
    const isOpen = completing?.id === checksheet.id;
    const weHoldLock = lock?.user === currentUser.publicId;

    if (isOpen && weHoldLock) {
      heldLockRef.current = true;
      relockingRef.current = false;
    }

    if (isOpen && heldLockRef.current && !weHoldLock) {
      // Phones drop the socket while the camera or file picker is open — take the lock back.
      if (!lock && lockSocketIdRef.current !== socket.id) {
        lockSocketIdRef.current = socket.id;
        relockingRef.current = true;
        socket.emit(LOCK, getFacilityRooms(slug, "checksheet"), key);
        return;
      }
      if (!lock && relockingRef.current) return;

      heldLockRef.current = false;
      setCompleting(undefined);
      setKeyHolder(null);
      return;
    }

    if (!isOpen) {
      heldLockRef.current = false;
      relockingRef.current = false;
    }

    if (lock && lock.user !== currentUser.publicId) {
      apiUsers.callGet(lock.user).then(({status, data}) => {
        if (status === 200) {
          const {firstName, lastName} = data;
          setKeyHolder(
            <Abbr title={`${firstName} ${lastName}`}>
              by {firstName}&nbsp;{lastName}
            </Abbr>
          );
        }
      });
    } else if (!lock) {
      setKeyHolder(null);
    }
  }, [
    isMounted,
    currentUser.publicId,
    locks,
    key,
    apiUsers,
    setCompleting,
    completing?.id,
    checksheet.id,
    socket,
    slug
  ]);

  const saveChecksheet = (
    record,
    draftId = null,
    isDraft = false,
    outOfRange = null,
    outOfRangeMultiple = null,
    notifyFieldCondition = null
  ) => {
    const params = {
      ...record,
      draft: isDraft,
      outOfRange: outOfRange ? Object.keys(outOfRange) : null,
      outOfRangeMultiple: outOfRangeMultiple ? Object.keys(outOfRangeMultiple) : null,
      notifyFieldCondition: notifyFieldCondition ? Object.keys(notifyFieldCondition) : null,
      withSharedResponses: true
    };

    if (activeChecksheet?.state && activeChecksheet.stateActive)
      params.stateId = activeChecksheet.state?.id;

    const success = draftId ? 200 : 201;
    const request = draftId
      ? apiChecksheetRecords.callPut(draftId, params)
      : apiChecksheetRecords.callPost(params);

    request.then(({status, data}) => {
      if (status === success && data) {
        const {data: updatedRecord} = data;

        socket.emit(
          RELEASE,
          getFacilityRooms(slug, "checksheet"),
          `checksheet_${updatedRecord.facilityChecksheetId}`
        );

        if (isDraft) {
          socket.emit(NOTIFY_DRAFT, room, `checksheet_${updatedRecord.facilityChecksheetId}`);
          updateChecksheetInfo(checksheet.id, updatedRecord, "recordDraft");
          setCompleting(undefined);
        } else {
          // Match Checksheet.js: force_send so NOTIFY_COMPLETED always broadcasts. Server gates on
          // FacilityChecksheet.completed() when force_send is false; that commonly stays false after
          // save (timing, overdue window, lingering draft queries), leaving tasks UI stuck in completing.
          socket.emit(
            NOTIFY_COMPLETED,
            room,
            `checksheet_${updatedRecord.facilityChecksheetId}`,
            true
          );
          updateChecksheetInfo?.(checksheet.id, null, "recordDraft");
          setCompleting(undefined);
          if (isOptional) {
            setLastCompletedAt(updatedRecord.completedAt);
            updateChecksheetInfo(checksheet.id, updatedRecord.completedAt, "lastCompletedAt");
          } else {
            setHide(prev =>
              !prev.includes(`${checksheet.id}`) ? [...prev, `${checksheet.id}`] : prev
            );
          }
          setSharedResponses(data.sharedResponses);
        }
      }

      setCompleteLoading(null);
      setDraftLoading(null);
    });
  };

  const buildChecksheetRecord = (
    responses,
    draftId = null,
    isDraft = false,
    outOfRange = null,
    outOfRangeMultiple = null,
    notifyFieldCondition = null
  ) => {
    const {id, name, frequency, builder} = activeChecksheet;

    const info = {facilityChecksheetId: id};

    if (isDraft) setDraftLoading(id);
    else setCompleteLoading(id);

    const {completedAt, ...rest} = responses;
    const formattedResponses = formatSubmissions(rest, builder);
    const submission = {
      name,
      responses: formattedResponses
    };

    if (frequency?.name) submission.frequency = frequency.name;

    const checksheetRecord = {...info, submission: {...submission}, completedAt};

    saveChecksheet(
      checksheetRecord,
      draftId,
      isDraft,
      outOfRange,
      outOfRangeMultiple,
      notifyFieldCondition
    );
  };

  const getCompletionRateHelper = () => {
    if (!checksheet?.recordDraft?.submission.responses) return 0;
    const {completedAt: _ca, ...rest} = {...checksheet.recordDraft.submission.responses};
    return getCompletionRate(rest);
  };

  const facilityStates = useMemo(() => {
    if (!allStates || Object.keys(allStates).length === 0) return null;
    return [
      ...Object.entries(allStates).map(([name, {id, color}]) => ({
        name: name.toUpperCase(),
        id: `${id}`,
        color
      }))
    ];
  }, [allStates]);

  const facilityState = useMemo(() => {
    if (!checksheet || !checksheet.state || !checksheet.stateActive || !facilityStates) return null;
    return facilityStates.find(({id}) => id === `${checksheet.state.facilityStateId}`) || null;
  }, [checksheet, facilityStates]);

  // Always opens a fresh form. recordDraft is cleared on submit, so
  // activeChecksheet.recordDraft is null here and buildChecksheetRecord's draftId resolves to
  // null, forcing saveChecksheet down the POST (never PUT) path.
  const clearOpeningIfMine = () => {
    if (openingIdRef?.current === checksheet.id) openingIdRef.current = null;
  };

  const openForm = () => {
    const weatherContext = toWeatherContext(checksheet?.weather, checksheet?.precipitationChance);
    const dateForConditional = dayjs.utc().tz(settings?.timezone || "US/Pacific");
    const gatedReviews = filterReviewsForGate(checksheet?.reviews, {
      dateForConditional,
      weatherContext
    });
    if (
      gatedReviews.length > 0 &&
      (!checksheet?.recordDraft || recordDraftUser?.publicId !== currentUser.publicId)
    )
      setShowModalReviewsRequired(true);

    lockSocketIdRef.current = socket.id;
    socket.emit(LOCK, getFacilityRooms(slug, "checksheet"), key);

    if (hasFullBuilder) {
      setCompleting(checksheet);
      return;
    }

    if (openingIdRef) openingIdRef.current = checksheet.id;
    setFetchLoading(true);
    fetchFullChecksheet()
      .then(response => {
        if (response?.status === 200 && response?.data) {
          updateChecksheetInfo(checksheet.id, response.data);
          setCompleting(response.data);
          return;
        }
        socket.emit(RELEASE, getFacilityRooms(slug, "checksheet"), key);
      })
      .catch(() => {
        socket.emit(RELEASE, getFacilityRooms(slug, "checksheet"), key);
      })
      .finally(() => {
        clearOpeningIfMine();
        setFetchLoading(false);
      });
  };

  return (
    <ChecksheetWrapper
      open={completing?.id && completing.id === checksheet.id}
      data-testid="checksheet.wrapper">
      <HeaderWrapper>
        {checksheet.name && (
          <ChecksheetName>
            <Abbr title={checksheet.name}>{checksheet.name.toUpperCase()}</Abbr>
          </ChecksheetName>
        )}
        <Pills>
          {facilityState && (
            <Pill color={facilityState?.color ? `#${facilityState.color}` : undefined}>
              <Abbr title={facilityState?.name || "DEFAULT"}>
                {facilityState?.name || "DEFAULT"}
              </Abbr>
            </Pill>
          )}
          {checksheet?.frequency?.name && (
            <Frequency>
              <Abbr title={checksheet.frequency.name}>{checksheet.frequency.name}</Abbr>
            </Frequency>
          )}
          {checksheet?.recordDraft && checksheet?.recordDraft.submission && recordDraftUser && (
            <Draft>
              <Abbr title="INCOMPLETE">
                INCOMPLETE&nbsp;
                {getCompletionRateHelper(checksheet?.recordDraft?.submission.responses)}%
              </Abbr>
            </Draft>
          )}
        </Pills>
      </HeaderWrapper>

      {checksheet && (!completing || completing.id !== checksheet.id) && (
        <CompleteWrapper>
          {checksheet.dateDue && (
            <DueDate>
              Due&nbsp;
              {prettyDateInUserTimezone(checksheet.dateDue, settings.timezone, "MMM DD, YYYY")}
            </DueDate>
          )}
          {isOptional && lastCompletedAt && (
            <DueDate data-testid="checksheetsDue.lastCompletedAt">
              Last submitted&nbsp;
              {prettyDateInUserTimezone(lastCompletedAt, settings.timezone, "MMM D, YYYY h:mm A")}
            </DueDate>
          )}
          {roleCanAccessResource("facility_checksheet_record", "create") && (
            <Complete>
              {locks && locks[key] && currentUser.publicId !== locks[key].user ? (
                <Text data-testid="checksheetsDue.locked">
                  {locks[key].percentage > 0 && (
                    <>
                      <Small>(Progress:&nbsp;{locks[key].percentage}%)</Small> <br />
                    </>
                  )}
                  <FontAwesomeIcon icon={faLock} />
                  &nbsp;Locked&nbsp;
                  {keyHolder}
                </Text>
              ) : (
                <>
                  {!isMobile() && atLeast("admin") && (
                    <Button
                      type="button"
                      disabled={fetchLoading}
                      onClick={() => {
                        if (hasFullBuilder) {
                          setShowModalReviews(true);
                          return;
                        }
                        if (openingIdRef) openingIdRef.current = checksheet.id;
                        setFetchLoading(true);
                        fetchFullChecksheet()
                          .then(response => {
                            if (response?.status === 200 && response?.data) {
                              updateChecksheetInfo(checksheet.id, response.data);
                              setShowModalReviews(true);
                            }
                          })
                          .finally(() => {
                            clearOpeningIfMine();
                            setFetchLoading(false);
                          });
                      }}>
                      {checksheet.reviews?.length > 0 && (
                        <Badge
                          count={checksheet.reviews?.length}
                          title="Required reviews"
                          offset="14px"
                          color={heroTheme.warning}
                        />
                      )}
                      <FontAwesomeIcon icon={faTriangleExclamation} />
                    </Button>
                  )}
                  <Button
                    type="button"
                    data-testid="checksheetsDue.complete"
                    disabled={fetchLoading}
                    onClick={openForm}>
                    Complete
                  </Button>
                </>
              )}
            </Complete>
          )}
        </CompleteWrapper>
      )}

      {completing?.id &&
        completing.id === checksheet.id &&
        activeChecksheet?.builder?.allIds?.length > 0 && (
          <CompletingSection>
            <PrecipitationChanceIndicator
              precipitationChance={activeChecksheet.precipitationChance}
              windDirection={activeChecksheet.windDirection}
              windSpeed={activeChecksheet.windSpeed}
              links={getWeatherSourceLinks(facility?.primaryAddress || {})}
            />
            <RenderChecksheet
              room={room}
              task={activeChecksheet}
              responses={
                activeChecksheet?.recordDraft
                  ? activeChecksheet.recordDraft.submission.responses
                  : null
              }
              sharedResponses={sharedResponses}
              previousSubmission={prevSubmission}
              setSubmission={(responses, outOfRange, outOfRangeMultiple, notifyFieldCondition) =>
                buildChecksheetRecord(
                  responses,
                  activeChecksheet.recordDraft ? activeChecksheet.recordDraft.id : null,
                  false,
                  outOfRange,
                  outOfRangeMultiple,
                  notifyFieldCondition
                )
              }
              setDraft={(responses, outOfRange, outOfRangeMultiple, notifyFieldCondition) =>
                buildChecksheetRecord(
                  responses,
                  activeChecksheet.recordDraft ? activeChecksheet.recordDraft.id : null,
                  true,
                  outOfRange,
                  outOfRangeMultiple,
                  notifyFieldCondition
                )
              }
              completeLoading={completeLoading === checksheet.id}
              draftLoading={draftLoading === checksheet.id}
              dateDue={activeChecksheet.dateDue}
              startDate={activeChecksheet.startDate ?? activeChecksheet.dateDue}
              showDates
              cancelFunction={() => {
                socket.emit(RELEASE, getFacilityRooms(slug, "checksheet"), key);
                setCompleting(undefined);
              }}
              user={
                recordDraftUser ? `${recordDraftUser.firstName} ${recordDraftUser.lastName}` : null
              }
              completedAt={
                activeChecksheet.recordDraft ? activeChecksheet.recordDraft.completedAt : null
              }
              draftDate={
                activeChecksheet.recordDraft ? activeChecksheet.recordDraft.createdAt : null
              }
              readOnly={!locks || (locks[key] && locks[key].user !== currentUser.publicId)}
              preview={false}
              persistToLocalStorage
              allowDeleteFromLocalStorage
              freezeComponentState={false}
            />
          </CompletingSection>
        )}

      {showModalReviewsRequired && targetRecord && (
        <ModalRecordView
          visible={showModalReviewsRequired}
          setVisible={setShowModalReviewsRequired}
          hasBackButton
          goBack={() => setTargetRecord(undefined)}
          checksheet={checksheet}
          record={targetRecord}
        />
      )}

      {showModalReviewsRequired && !targetRecord && (
        <ModalReview
          visible={showModalReviewsRequired}
          setVisible={setShowModalReviewsRequired}
          checksheet={checksheet}
          confirm={() => setShowModalReviewsRequired(false)}
          previewRecord={setTargetRecord}
        />
      )}

      {showModalReviews && (
        <ModalReviews
          visible={showModalReviews}
          setVisible={setShowModalReviews}
          checksheet={checksheet}
        />
      )}
    </ChecksheetWrapper>
  );
};

ChecksheetDue.propTypes = {
  room: PropTypes.string,
  locks: PropTypes.objectOf(PropTypes.any),
  checksheet: PropTypes.objectOf(PropTypes.any).isRequired,
  sharedResponses: PropTypes.objectOf(PropTypes.any),
  completing: PropTypes.objectOf(PropTypes.any),
  setCompleting: PropTypes.func.isRequired,
  setHide: PropTypes.func.isRequired,
  setSharedResponses: PropTypes.func.isRequired,
  openingIdRef: PropTypes.objectOf(PropTypes.any)
};

// Style Overrides
const ChecksheetWrapper = styled.section`
  ${flex("row", "nowrap", "space-between", "stretch")};
  position: relative;
  width: 100%;
  border: ${border} solid ${({theme}) => theme.primary};
  border-radius: ${radius};
  padding: ${pad}px;
  margin-top: ${pad / 2}px;

  ${bp(3)} {
    margin-bottom: ${pad}px;
  }

  ${({open}) =>
    open &&
    css`
      flex-wrap: wrap;
    `}
`;

const HeaderWrapper = styled(Inline)`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  width: 70%;
  gap: unset;

  ${bp(2)} {
    width: calc(100% - 170px);
  }
`;

const CompleteWrapper = styled.div`
  ${flex("column", "nowrap", "space-between")};
  width: 30%;
`;

const ChecksheetName = styled(Title)`
  align-self: start;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  max-width: 90%;
`;

const Pills = styled.div`
  display: flex;
  align-self: start;
  gap: 7px;
`;

const Draft = styled(Pill)`
  background: ${({theme}) => theme.warning};
`;

const Frequency = styled(Pill)`
  background: ${({theme}) => theme.primary};
`;

const DueDate = styled(Text)`
  display: none;

  ${bp(1)} {
    display: block;
    position: relative;
    text-align: right;
    padding: 0;
    margin-bottom: ${pad}px;
    width: 100%;
  }
`;

const CompletingSection = styled.div`
  width: 100%;
  flex-basis: 100%;
`;

const Complete = styled(Inline)`
  justify-content: end;
  align-self: end;

  ${Text} {
    text-align: right;
  }
`;

export default ChecksheetDue;
